import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { IContract, IContractAccountsCollection } from '@models/contract.class';
import { EventError } from '@models/tracking-error-event.class';
import { EventSuccess } from '@models/tracking-success-event.class';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { AppState, TRACKING_EVENTS } from 'src/app';
import { ContractsReceived } from 'src/app/state/actions/contracts-com.action';
import { selectCurrentContract } from 'src/app/state/selectors/current-contract.selectors';
import { ContractsService, NotificationService } from 'src/core';
import { MixpanelService } from 'src/core/services/mixpanel.service';

@Component({
  selector: 'app-strategy-edition',
  templateUrl: './strategyEdition.component.html',
  styleUrls: ['./strategyEdition.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StrategyEditionComponent implements OnInit, OnDestroy {
  @Input() strategy: string;
  @Output() onHide: EventEmitter<boolean> = new EventEmitter();

  public strategyName: string;
  public loading: boolean;

  private currentContractSubscription: Subscription;
  private contractAccountSubscription: Subscription;
  private selectedContract: string;
  private strategySelected: any;
  private contractAccountsCollection: IContractAccountsCollection;
  private isUsaContract: boolean;
  private strategyNames: string[];
  private noSpecialCharacters: RegExp;

  constructor(
    private store: Store<AppState>,
    private contractService: ContractsService,
    private notificationService: NotificationService,
    private mixpanelService: MixpanelService,
  ) {
    this.strategyNames = [];
    this.noSpecialCharacters = new RegExp(
      /^[^\`\~\+\,\u00a1\!\u00bf\?\@\#\$\%\^\&\*\(\)\;\/\|\<\>\"\=\[\]\{\}\_\-\.\u00b4]*$/,
    );
  }

  get isInvalid(): boolean {
    return (
      !this.strategyName ||
      !this.strategyName?.length ||
      this.strategyName === this.strategySelected.name ||
      !this.isUniqueName ||
      !this.isValidName
    );
  }

  get isUniqueName(): boolean {
    return this.strategyNames.indexOf(this.strategyName?.trim()) === -1;
  }

  get isValidName(): boolean {
    return this.noSpecialCharacters.test(this.strategyName || '');
  }

  ngOnDestroy() {
    [this.currentContractSubscription, this.contractAccountSubscription]
      .filter((subscription) => !!subscription)
      .forEach((subscription) => subscription.unsubscribe());
  }

  ngOnInit() {
    this.currentContractSubscription = this.store
      .select(selectCurrentContract)
      .pipe(
        tap((value: IContract) => {
          this.isUsaContract = value.isUsa;
        }),
        map((value: IContract) => value.contractID),
        filter((contractId: any) => contractId !== undefined),
        distinctUntilChanged(),
      )
      .subscribe((contractId: any) => {
        const successEvent = new EventSuccess();
        successEvent.setType(this.isUsaContract);
        const successEventData = successEvent.getEventSuccess();

        this.mixpanelService.track(
          TRACKING_EVENTS.screen_edit_strategy.event,
          successEventData,
        );

        this.selectedContract = contractId;
        this.getContractAccount();
      });
  }

  onInput(input: HTMLInputElement): void {
    input.value = input.value.trimStart();
    this.strategyName = this.strategyName.trimStart();
  }

  hide() {
    this.onHide.next(true);
  }

  confirmationAction() {
    if (!this.strategyName.length) {
      return;
    }
    this.loading = true;
    const account = [
      { value: this.strategyName, path: '/name', op: 'replace' },
    ];

    this.contractService
      .patchAccount(
        this.strategySelected.contractId,
        this.strategySelected.accountId,
        account,
      )
      .subscribe(
        (response: any) => {
          this.loading = false;
          this.hide();

          const successEvent = new EventSuccess();
          successEvent.setType(this.isUsaContract);
          const successEventData = successEvent.getEventSuccess();

          this.mixpanelService.track(
            TRACKING_EVENTS.edit_strategy_name.event,
            successEventData,
          );
          this.notificationService.showSuccess({
            title: 'Cambio de nombre de estrategia',
            message: `Se cambio el nombre de tu estrategia a ${this.strategyName}`,
            eventId: undefined,
            isBusinessError: false,
          });

          this.contractAccountsCollection.accounts[this.selectedContract].name =
            this.strategyName;
          this.store.dispatch(
            new ContractsReceived(this.contractAccountsCollection),
          );
        },
        (error) => {
          this.loading = false;

          const eventError = new EventError();
          eventError.setType(this.isUsaContract);
          eventError.setCallback(error.message);
          const eventErrorData = eventError.getEventError();

          this.mixpanelService.track(
            TRACKING_EVENTS.edit_strategy_name.event,
            eventErrorData,
          );

          this.notificationService.showError({
            title: 'Cambio de nombre de estrategia',
            message: `No se pudo editar el nombre de la estrategia. Por favor inténtalo de nuevo.`,
            eventId: undefined,
            isBusinessError: false,
          });
        },
      );
  }

  private getContractAccount() {
    this.contractAccountSubscription = this.store
      .select('contractAccountsCollection')
      .pipe(
        map((contractsComMap: any) => contractsComMap.toJS()),
        filter(
          (contractsCom: {
            loaded: boolean;
            loading: boolean;
            data: IContractAccountsCollection;
          }) => contractsCom.loaded,
        ),
        map((collection) => collection.data),
        distinctUntilChanged((a, b) => isEqual(a, b)),
      )
      .subscribe((collection: IContractAccountsCollection) => {
        this.contractAccountsCollection = collection;
        this.strategySelected = collection.accounts[this.selectedContract];
        this.strategyName = this.strategySelected.name;
        Object.keys(this.contractAccountsCollection.accounts)
          .filter((key) => key !== this.selectedContract)
          .forEach((key) => {
            this.strategyNames.push(
              this.contractAccountsCollection.accounts[key].name.trim(),
            );
          });
      });
  }
}
